import { brandScaleFromHex, createBrandVars, type BrandScale } from '@aslanpay/portal-common-lib';

import getEnvironment from 'app/utils/environment';

export const BRAND_COLOR_PARAM = 'brandColor';

// Kept for the tab, so the colour survives navigating away from the URL that set it
const STORAGE_KEY = 'aslan.brand-color-override';

const rgbTriplet = (hex: string) =>
  [1, 3, 5].map((start) => parseInt(hex.slice(start, start + 2), 16)).join(', ');

const toStylesheet = (vars: Record<string, string>, scale: BrandScale) => {
  const portalVars: Record<string, string> = {
    '--chakra-colors-brand-indigo': 'var(--chakra-colors-brand-600)',
    '--chakra-colors-brand-indigoAccent': 'var(--chakra-colors-brand-500)',
    '--chakra-colors-brand-heroPurple': 'var(--chakra-colors-brand-900)',
    '--chakra-colors-brand-sidebarLilac': 'var(--chakra-colors-brand-50)',
    '--chakra-colors-brand-avatarLilac': 'var(--chakra-colors-brand-200)',
    '--chakra-colors-brand-navMuted': 'var(--chakra-colors-brand-200)',
    '--aslan-shadow-rgb': rgbTriplet(scale[900]),
    '--aslan-brand-rgb': rgbTriplet(scale[600]),
    '--aslan-artwork-subtle': 'var(--chakra-colors-brand-50)',
    '--aslan-artwork-ink': 'var(--chakra-colors-brand-600)',
    '--aslan-artwork-strong': 'var(--chakra-colors-brand-900)',
    '--aslan-artwork-400': 'var(--chakra-colors-brand-400)',
    '--aslan-artwork-500': 'var(--chakra-colors-brand-500)',
    '--aslan-artwork-600': 'var(--chakra-colors-brand-600)',
    '--aslan-artwork-700': 'var(--chakra-colors-brand-700)',
    '--aslan-artwork-900': 'var(--chakra-colors-brand-900)',
  };
  const declarations = Object.entries({ ...vars, ...portalVars })
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n');
  return `:root:root {\n${declarations}\n}`;
};

/**
 * The co-brand stylesheet for a colour, as an uploaded one would be (see co-brand.example.css):
 * createBrandVars' variables plus the portal's own. `:root:root` outranks a company's stylesheet.
 * Null for anything but a #RRGGBB colour.
 */
export const brandColorCss = (hex: string): string | null => {
  const vars = createBrandVars(hex);
  if (vars) {
    return toStylesheet(vars, brandScaleFromHex(hex.trim()));
  }
  return null;
};

/**
 * The `?brandColor=<hex>` testing override, outside prod only: from the URL, else what an earlier
 * URL in this tab set. Null when there is none or it is not a #RRGGBB colour.
 */
export const readBrandColorOverride = (): string | null => {
  if (getEnvironment() === 'Production') {
    return null;
  }
  try {
    const fromUrl = new URLSearchParams(window.location.search).get(BRAND_COLOR_PARAM);
    if (fromUrl) {
      sessionStorage.setItem(STORAGE_KEY, fromUrl);
    }
    const hex = fromUrl ?? sessionStorage.getItem(STORAGE_KEY);
    return hex && createBrandVars(hex) ? hex.trim() : null;
  } catch {
    return null;
  }
};
